// Add only to a duplicated SOURCE theme after configuring both Shopify stores.
(function () {
  "use strict";
  const config = window.PMP_CHECKOUT_CONFIG;
  if (!config || config.enabled !== true) return;
  const pending = new Set();
  const originalFetch = window.fetch.bind(window);
  window.fetch = function (input, options) {
    const url = new URL(typeof input === 'string' || input instanceof URL ? input : input.url, location.href);
    const method = String(options?.method || input?.method || 'GET').toUpperCase();
    const result = originalFetch(input, options);
    if (url.origin === location.origin && method !== 'GET' && /\/cart\/(add|change|update|clear)(\.js)?$/.test(url.pathname)) {
      pending.add(result);
      result.then(() => pending.delete(result), () => pending.delete(result));
    }
    return result;
  };
  window.PMPCartUpdatesReady = async function () {
    for (let attempt = 0; pending.size && attempt < 10; attempt++) await Promise.allSettled([...pending]);
    if (pending.size) throw new Error('CART_UPDATE_PENDING');
  };
  let busy = false;
  let transferId;
  const messages = {
    EN: "We couldn't open checkout. Your cart is saved. Please try again or contact us.",
    FR: "Le paiement n’a pas pu s’ouvrir. Votre panier est conservé. Réessayez ou contactez-nous."
  };

  function ajaxMinor(value, digits) {
    // Shopify Ajax Cart amounts use a 100x representation, including zero-decimal currencies.
    if (!Number.isSafeInteger(value) || !Number.isInteger(digits) || digits < 0 || digits > 3) throw new Error("INVALID_PRICE");
    const result = BigInt(value) * 10n ** BigInt(digits);
    if (result % 100n) throw new Error("INVALID_PRICE");
    const minor = Number(result / 100n);
    if (!Number.isSafeInteger(minor)) throw new Error("INVALID_PRICE");
    return minor;
  }

  function permitted(url) {
    return url.protocol === "https:" && !url.username && !url.password && !url.port &&
      config.allowedCheckoutHosts.includes(url.hostname);
  }

  function recoveryEvent(cart, url, tracking) {
    // The existing SOURCE Omnisend snippet owns identification and delivery.
    // The exact target checkout preserves bundle lines and the accepted code.
    if (!tracking.consent.marketing) return;
    try {
      const key = 'pmp:omnisend-checkout:' + transferId;
      if (window.sessionStorage?.getItem(key)) return;
      if (typeof window.omnisend?.push !== 'function') return;
      const absolute = value => {
        try { const u = new URL(value, location.origin); return u.protocol === 'https:' ? u.href : ''; }
        catch { return ''; }
      };
      const payload = {
        origin: 'api', eventID: transferId, eventVersion: '',
        properties: {
          abandonedCheckoutURL: url.href, raw: { abandoned_checkout_url: url.href },
          cartID: String(cart.token || transferId).split('?')[0],
          currency: cart.currency, value: Number(cart.total_price) / 100,
          lineItems: cart.items.map(item => ({
            productID: String(item.product_id || item.variant_id), productVariantID: String(item.variant_id),
            productTitle: String(item.product_title || item.title || ''),
            productVariantTitle: String(item.variant_title || ''),
            productSKU: String(item.sku || ''), productQuantity: item.quantity,
            productPrice: Number(item.final_price || 0) / 100,
            productStrikeThroughPrice: Number(item.original_price || item.price || 0) / 100,
            productImageURL: absolute(item.image || item.featured_image?.url || ''),
            productURL: absolute(item.url || '/products/' + item.handle)
          }))
        }
      };
      // Contact is intentionally omitted: the SDK uses its already identified
      // contact (email click/form/login), without guessing anyone's identity.
      window.omnisend.push(['track', 'started checkout', payload]);
      window.sessionStorage?.setItem(key, '1');
    } catch { /* Email tracking must never prevent a customer from paying. */ }
  }

  async function trackingContext() {
    const privacy = window.Shopify?.customerPrivacy;
    const consent = { marketing: privacy?.marketingAllowed?.() === true && privacy?.saleOfDataAllowed?.() === true, analytics: privacy?.analyticsProcessingAllowed?.() === true };
    const attribution = {}, identity = {};
    if (consent.marketing) {
      let paid;
      try { paid = JSON.parse(window.localStorage?.getItem('pmp:attribution') || '{}').lastPaid; } catch {}
      if (paid?.capturedAt > Date.now() - 90 * 86400000 && paid.capturedAt <= Date.now()) {
        for (const key of ['source','medium','campaign','term','content']) if (typeof paid[key] === 'string') attribution['utm_' + key] = paid[key].slice(0, 255);
        for (const key of ['gclid','gbraid','wbraid','fbclid']) if (typeof paid.clickIds?.[key] === 'string') attribution[key] = paid.clickIds[key].slice(0, 255);
      }
      const params = new URLSearchParams(location.search);
      const currentPaid=['gclid','gbraid','wbraid','fbclid'].some(key=>params.has(key)) || /^(cpc|ppc|paid|paid_social|paid_search|retargeting|remarketing|display)$/i.test(params.get('utm_medium')||'');
      if(currentPaid)for(const key of Object.keys(attribution))delete attribution[key];
      if(currentPaid || !Object.keys(attribution).length)for (const key of ['utm_source','utm_medium','utm_campaign','utm_term','utm_content','gclid','gbraid','wbraid','fbclid']) {
        const value = params.get(key); if (value && value.length <= 255) attribution[key] = value;
      }
      for (const key of ['fbp','fbc']) {
        const value = String(document.cookie || '').split(';').map(s=>s.trim()).find(s=>s.startsWith('_'+key+'='))?.split('=').slice(1).join('=');
        if (value && value.length <= 255) identity[key] = value;
      }
    }
    if (consent.analytics) {
      const get = field => new Promise(resolve => {
        if (typeof window.gtag !== 'function') return resolve('');
        const timer = setTimeout(()=>resolve(''), 700);
        window.gtag('get', 'G-KKS5T7SPHR', field, value=>{clearTimeout(timer);resolve(String(value || ''));});
      });
      [identity.ga_client_id, identity.ga_session_id] = await Promise.all([get('client_id'),get('session_id')]);
      if (!identity.ga_client_id) {
        const cookie=String(document.cookie || '').split(';').map(s=>s.trim()).find(s=>s.startsWith('_ga='));
        const match=cookie?.match(/^_ga=GA\d+\.\d+\.(\d+\.\d+)$/); if(match) identity.ga_client_id=match[1];
      }
    }
    return {consent,attribution,identity};
  }

  function journeyContext(cart, tracking) {
    const {consent,attribution}=tracking;
    if (!(consent.analytics || consent.marketing)) return {};
    const now=Date.now(), ttl=90*86400000, result={};
    const text=value=>typeof value==='string'?value.replace(/[\u0000-\u001f\u007f]/g,' ').trim().slice(0,255):'';
    const page=value=>{
      try { const url=new URL(value); return ['https:','http:'].includes(url.protocol)?(url.origin+url.pathname).slice(0,255):''; } catch { return ''; }
    };
    const date=value=>{const n=typeof value==='number'?value:Date.parse(value);return Number.isFinite(n)&&n>now-ttl&&n<=now?n:0;};
    const paidMedium=/^(cpc|ppc|paid|paidsocial|paid[-_ ]?social|paid[-_ ]?search|retargeting|remarketing|display)$/i;
    const touchFields=['landing','referrer','source','medium','campaign','content','term','at','channel'];
    const keys=['pmp_journey_id','tracking_version','attribution_model','attribution_basis','seo_organic','entry_page','entry_basis','entry_page_type','entry_page_handle','entry_referrer','entry_channel','entry_source','entry_medium',...['attribution','first_entry','first_touch','first_free','last_paid'].flatMap(prefix=>touchFields.map(field=>prefix+'_'+field))];
    for(const key of keys) if(typeof cart.attributes?.[key]==='string') result[key]=text(cart.attributes[key]);
    let state={}, legacy={}, sessionLanding='', sessionReferrer='';
    try { state=JSON.parse(window.localStorage?.getItem('pmp:attribution') || '{}') || {}; } catch {}
    if(consent.marketing)try {legacy=JSON.parse(window.localStorage?.getItem('pmp_paid_attribution_v3') || '{}') || {};} catch {}
    try {sessionLanding=window.sessionStorage?.getItem('pmp_landing_url') || '';sessionReferrer=window.sessionStorage?.getItem('pmp_first_referrer') || '';} catch {}
    function touch(value, requireDate=false) {
      if(!value || typeof value!=='object') return null;
      const capturedAt=date(value.capturedAt || value.captured_at || value.at);
      if(requireDate && !capturedAt) return null;
      const rawLanding=value.landingUrl || value.landing_url || value.landing || '';
      const landing=page(rawLanding), referrer=page(value.referrer || '');
      let params;try {params=new URL(rawLanding).searchParams;}catch{params=new URLSearchParams();}
      let source=text(value.source || value.utm_source || params.get('utm_source'));
      let medium=text(value.medium || value.utm_medium || params.get('utm_medium'));
      const hasClick=['gclid','gbraid','wbraid','fbclid'].some(key=>value.clickIds?.[key] || value[key] || params.get(key));
      const paid=paidMedium.test(medium) || hasClick;
      let channel='';
      if(paid && !consent.marketing) {source='';medium='';channel='inconnue (consentement marketing absent)';}
      else if(paid) channel=paidMedium.test(medium)?'Paid traffic (UTM)':'Paid click signal (inferred)';
      else {
        let host='';try{host=new URL(referrer).hostname.toLowerCase();}catch{}
        const internal=/^(?:(?:www|pay|checkout)\.)?puremajestypet\.com$/.test(host) || /^(?:www\.)?puremajestypets\.com$/.test(host) || host.endsWith('.myshopify.com');
        if(!source && /(^|\.)google\.(?:com|[a-z]{2,3}|com\.[a-z]{2}|co\.[a-z]{2})$/.test(host)) {source='google';medium='organic';channel='Google organic search / SEO (inferred)';}
        else if(!source && /(^|\.)bing\.com$/.test(host)) {source='bing';medium='organic';channel='Bing organic search / SEO (inferred)';}
        else if(source && medium==='organic') channel='Organic traffic (UTM)';
        else if(source) channel=medium || 'Campaign tagging (UTM)';
        else if(host && !internal) {source=host;medium='referral';channel='Referral (inferred)';}
        else if(landing) {source='direct';medium='none';channel='Direct / no external referrer (inferred)';}
      }
      if(!landing && !source && !referrer) return null;
      const record={landing,referrer,source,medium,channel,paid};
      if(capturedAt)record.at=new Date(capturedAt).toISOString();
      if(consent.marketing)for(const field of ['campaign','content','term'])record[field]=text(value[field] || value['utm_'+field] || params.get('utm_'+field));
      return record;
    }
    function write(prefix,record) {
      if(!record) return;
      for(const field of touchFields) {const value=text(record[field]);if(value)result[prefix+'_'+field]=value;else delete result[prefix+'_'+field];}
    }
    const session=touch({landingUrl:sessionLanding,referrer:sessionReferrer});
    const firstEntry=touch(state.firstEntry,true) || session;
    const firstFree=touch(state.firstFree,true) || (firstEntry && !firstEntry.paid?firstEntry:null);
    let lastPaid=consent.marketing?(touch(state.lastPaid,true) || touch(legacy,true)):null;
    if(lastPaid && !lastPaid.paid)lastPaid=null;
    const params=new URLSearchParams(location.search);
    const currentPaid=['gclid','gbraid','wbraid','fbclid'].some(key=>params.has(key)) || paidMedium.test(params.get('utm_medium') || '');
    if(consent.marketing && currentPaid)lastPaid=touch({landingUrl:location.href,referrer:document.referrer || '',capturedAt:now,...attribution});
    const selected=lastPaid || firstFree || firstEntry;
    if(selected) {
      result.tracking_version='pmp_shopify_v1';result.attribution_model='last_paid_else_first_free_v1';
      result.attribution_basis=lastPaid?'last_paid_click':firstFree?'first_free_click':session===firstEntry?'session_entry':'first_entry';
      write('attribution',selected);write('first_entry',firstEntry);write('first_touch',firstEntry);write('first_free',firstFree);
      if(lastPaid)write('last_paid',lastPaid);
      const entry=firstEntry || firstFree || lastPaid;
      if(entry) {
        result.entry_page=entry.landing;result.entry_basis=firstEntry?'first_entry':firstFree?'first_free':'last_paid';
        result.entry_referrer=entry.referrer;result.entry_source=entry.source;result.entry_medium=entry.medium;result.entry_channel=entry.channel;
        if(entry.landing) {
          const parts=new URL(entry.landing).pathname.split('/').filter(Boolean);
          if(/^[a-z]{2}(?:-[a-z]{2})?$/i.test(parts[0] || ''))parts.shift();
          result.entry_page_type=({products:'product',blogs:'article',collections:'collection',pages:'page'})[parts[0]] || (parts[0] || 'home');
          result.entry_page_handle=parts.slice(parts[0]==='blogs'?2:1).join('/');
        }
      }
      result.seo_organic=selected.medium==='organic'?(/inferred/i.test(selected.channel)?'yes (inferred)':'yes (UTM)'):'no';
      if(text(state.journeyId))result.pmp_journey_id=text(state.journeyId);
    }
    return result;
  }

  async function start(button) {
    if (busy) return false;
    busy = true;
    button?.setAttribute("aria-busy", "true");
    const controls = [...document.querySelectorAll("[data-pmp-checkout]")];
    const previous = controls.map(element => [element, element.disabled]);
    controls.forEach(element => { if ("disabled" in element) element.disabled = true; });
    try {
      if (window.PMPCartUpdatesReady) await window.PMPCartUpdatesReady();
      const cartResponse = await fetch((window.Shopify?.routes?.root || "/") + "cart.js", {
        credentials: "same-origin", cache: "no-store"
      });
      if (!cartResponse.ok) throw new Error("CART_UNAVAILABLE");
      const cart = await cartResponse.json();
      const digits = config.currencyMinorUnits[cart.currency];
      if (digits === undefined || !cart.items?.length) throw new Error("INVALID_CART");
      const codes = [...new Set((cart.cart_level_discount_applications || [])
        .concat(cart.items.flatMap(item => (item.line_level_discount_allocations || []).map(a => a.discount_application)))
        .filter(application => application?.type === "discount_code").map(application => application.title))];
      transferId ||= crypto.randomUUID();
      const tracking = await trackingContext();
      const response = await fetch(config.bridgeEndpoint, {
        method: "POST", credentials: "omit", headers: { "Content-Type": "application/json" },
        signal: AbortSignal.timeout(18000),
        body: JSON.stringify({
          country: config.country, language: config.language, currency: cart.currency,
          handoffId: transferId, discountCodes: codes, ...tracking,
          journeyAttributes: journeyContext(cart,tracking), sourceCartNote: typeof cart.note==='string'?cart.note:'',
          expectedSubtotalMinor: ajaxMinor(cart.items_subtotal_price, digits),
          expectedCartDiscountMinor: ajaxMinor(cart.items_subtotal_price - cart.total_price, digits),
          lines: cart.items.map(item => ({
            sourceVariantId: String(item.variant_id), quantity: item.quantity, properties: item.properties || {},
            sellingPlanId: item.selling_plan_allocation?.selling_plan?.id || null
          }))
        })
      });
      const result = await response.json();
      if (!response.ok || typeof result.checkoutUrl !== "string") throw new Error(result.error || "CHECKOUT_UNAVAILABLE");
      const url = new URL(result.checkoutUrl);
      if (!permitted(url)) throw new Error("INVALID_DESTINATION");
      recoveryEvent(cart, url, tracking);
      // Use a real cross-domain link so Google's configured linker can decorate the click.
      // Do not use location.assign: it can bypass Google's click decoration.
      const link = document.createElement("a");
      link.href = url.href;
      link.hidden = true;
      document.body.appendChild(link);
      // One source owner only. This custom event is not a Purchase and emits no ad event by itself.
      window.dispatchEvent(new CustomEvent("pmp:checkout-handoff", { detail: { handoffId: transferId } }));
      link.click();
      link.remove();
      return true;
    } catch (error) {
      // Preserve the cart; don't silently route the buyer back to another payment provider.
      const message = messages[config.language] || messages.EN;
      const element = document.querySelector("[data-pmp-checkout-error]");
      if (element) { element.textContent = message; element.hidden = false; }
      else window.alert(message);
      return false;
    } finally {
      previous.forEach(([element, disabled]) => { if ("disabled" in element) element.disabled = disabled; });
      button?.removeAttribute("aria-busy");
      busy = false;
    }
  }

  document.addEventListener("click", event => {
    const button = event.target?.closest?.('[data-pmp-checkout], [name="checkout"], a[href*="/checkout"]');
    if (!button) return;
    if (button.href) {
      const destination = new URL(button.href, location.href);
      if (destination.origin !== location.origin) return;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
    void start(button);
  }, true);
  document.addEventListener("submit", event => {
    if (!event.submitter?.matches('[data-pmp-checkout], [name="checkout"]')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    void start(event.submitter);
  }, true);
  window.PMPCheckout = { start }; // Allows the existing drawer/sticky CTA to await cart changes first.
})();
